import React from 'react';
import Navbar from '../../../components/Navbar';
import HeroSection from '../components/HeroSection';
import ServicesSection from '../components/ServicesSection';
import CapabilitiesSection from '../components/CapabilitiesSection';
import ProcessSection from '../components/ProcessSection';
import CTASection from '../components/CTASection';
import Footer from '../components/Footer';
import './ConsultingLanding.css';
import '../../../components/Button.css';
import '../../../components/Container.css';

// Data configuration - can be moved to a separate file or API
const navbarLinks = [
  { href: '#about', label: 'About' },
  { href: '#services', label: 'Services' },
  { href: '#capabilities', label: 'Capabilities' },
  { to: '/platform', label: 'Platform' },
  { to: '/contact', label: 'Contact Us', isButton: true },
];

const services = [
  {
    icon: '🤖',
    title: 'Applied AI',
    description: 'We design and ship AI solutions that Retail and CPG teams actually use—demand forecasting, pricing, assortment, personalization, and supply chain. From use-case selection to production, we make AI pay off.',
    features: [
      'Use-case identification & ROI',
      'Forecasting, pricing & personalization',
      'Model development & evaluation',
      'Production deployment & monitoring',
    ],
    featured: true,
    // featuredBadge: 'Most Popular',
  },
  {
    icon: '🏗️',
    title: 'Data Foundations',
    description: 'AI only works if the data does. We build the platforms, pipelines, and quality layer Retail and CPG companies need to train, run, and trust AI—across products, channels, and customers.',
    features: [
      'Cloud data platforms',
      'Retail & CPG data models',
      'Real-time & batch pipelines',
      'Data quality & governance',
    ],
  },
  {
    icon: '🔌',
    title: 'Integrations',
    description: 'Connect into the systems your teams already run—ERP, POS, e-commerce, planning, and marketing. Insights land where merchandisers, planners, and operators make decisions.',
    features: [
      'POS, ERP & e-commerce',
      'Planning & replenishment',
      'CDP & marketing platforms',
      'Custom APIs & workflows',
    ],
  },
];

const capabilities = [
  {
    icon: '🤖',
    title: 'Applied AI & ML',
    description: 'Build and deploy models that improve demand, margin, and operations for Retail and CPG',
  },
  {
    icon: '✨',
    title: 'Generative AI',
    description: 'LLMs for merchandising, customer service, content, and internal knowledge',
  },
  {
    icon: '📈',
    title: 'Demand & Inventory Intelligence',
    description: 'Forecasting, replenishment, and allocation built around how Retail and CPG actually operate',
  },
  {
    icon: '🎯',
    title: 'Personalization & Pricing',
    description: 'Shopper experiences and price decisions grounded in your first-party data',
  },
  {
    icon: '🏬',
    title: 'Retail & CPG Data Platforms',
    description: 'Warehouses, lakes, and models organized around products, channels, and customers',
  },
  {
    icon: '🔗',
    title: 'Enterprise Integrations',
    description: 'Connect POS, ERP, e-commerce, planning, and marketing systems into one AI-ready stack',
  },
  {
    icon: '🚀',
    title: 'MLOps & Monitoring',
    description: 'Keep models and pipelines reliable, observable, and cost-efficient in production',
  },
  {
    icon: '🛡️',
    title: 'Governance & Security',
    description: 'Access, quality, and compliance so AI runs on data you can trust',
  },
];

const processSteps = [
  {
    number: '01',
    title: 'Discover',
    description: 'We map commercial goals, existing systems, and where AI can move demand, margin, or operations',
  },
  {
    number: '02',
    title: 'Design',
    description: 'Define the use cases, data architecture, and rollout plan that fit how your teams work',
  },
  {
    number: '03',
    title: 'Build',
    description: 'Develop models, pipelines, and integrations with regular check-ins against business outcomes',
  },
  {
    number: '04',
    title: 'Launch',
    description: 'Put into production with monitoring, documentation, and enablement for your teams',
  },
];

const footerSections = [
  {
    title: 'Services',
    links: [
      { label: 'Applied AI' },
      { label: 'Data Foundations for AI' },
      { label: 'AI Integrations' },
      { label: 'Consulting' },
    ],
  },
  {
    title: 'Company',
    links: [
      { href: '#about', label: 'About Us' },
      { href: '#services', label: 'Services' },
      { href: '#capabilities', label: 'Capabilities' },
      // { to: '/app', label: 'Platform' },
    ],
  },
  {
    title: 'Connect',
    links: [
      { to: '/contact', label: 'Contact Us' },
    ],
  },
];

const ConsultingLanding: React.FC = () => {
  return (
    <div className="consulting-app">
      <Navbar
        logo={{ src: '/orca-logo.png', text: 'Orca Labs' }}
        links={navbarLinks}
        overlay
      />

      <main>
        <HeroSection
          id="about"
          title="AI that works for"
          titleGradient=" Retail & CPG"
        />

        <ServicesSection services={services} />

        <CapabilitiesSection capabilities={capabilities} />

        <ProcessSection steps={processSteps} />

        <CTASection
          title="Ready to put AI to work in Retail & CPG?"
          description="Let's talk about the AI and data capabilities that will move demand, margin, and the shopper experience."
          actions={[
            { label: 'Get Started Today', to: '/contact', variant: 'primary' }
          ]}
        />
      </main>

      <Footer
        logo={{ src: '/orca-logo.png', text: 'Orca Labs' }}
        tagline="AI & data for Retail and CPG."
        sections={footerSections}
      />
    </div>
  );
};

export default ConsultingLanding;
